<template>
  <FriedModal :size="modalSize" @close="$emit('close')">
    <template v-if="showChangeSignerSelectSection || transferred" #container>
      <FriedModalSubContainer v-if="transferred">
        <template #header>
          <FriedModalHeader
            :header="$t('change_signer_success_title')"
            :description="$t('change_signer_success_description')"
          />
        </template>
        <template #footer>
          <FriedModalButtonsFooter>
            <FriedButton data-external="invite-modal-close-btn" @click="$emit('close')">
              {{ $t('cc_got_it_button') }}
            </FriedButton>
          </FriedModalButtonsFooter>
        </template>
      </FriedModalSubContainer>
      <FriedModalSubContainer v-else>
        <template #header>
          <FriedModalHeader
            :header="$t('change_signer_title')"
            :description="$t('change_signer_description')"
          />
        </template>
        <div class="change-signer-container">
          <FriedMessage
            v-if="errorMessage"
            type="error"
            class="full-width"
            :title="errorMessage.title"
          >
            <FriedParagraph>{{ errorMessage.message }}</FriedParagraph>
          </FriedMessage>
          <FriedSelect
            v-model="selectedRecipient"
            data-external="change-signer-select"
            :options="recipientOptions"
            fixed-label
            searchable
            option-label="displayName"
            :label="$t('recipients')"
            :placeholder="$t('change_signer_placeholder', { count: recipientOptions.length })"
            @search="searchInput = $event"
          >
            <template v-if="!isRecipientThresholdReached && !!searchInput" #customBottomOption>
              <FriedSelectOption @click="handleAddFromSearch">
                <FriedIcon icon="user-add" />
                {{ $t('add_new_recipient_label', { name: searchInput }) }}
              </FriedSelectOption>
            </template>
            <template v-if="!isRecipientThresholdReached" #emptyState><span /></template>
          </FriedSelect>
          <FriedParagraph v-if="!isRecipientThresholdReached">
            {{ $t('change_signer_help_text') }}
            <FriedLink @click="showChangeSignerSelectSection = false">
              {{ $t('change_signer_help_text_link') }}
            </FriedLink>
          </FriedParagraph>
        </div>
        <template #footer>
          <FriedModalButtonsFooter>
            <FriedButton
              data-external="transfer-cancel-button"
              button-type="secondary"
              :disabled="transferring"
              @click="$emit('close')"
            >
              {{ $t('cancel') }}
            </FriedButton>
            <FriedButton
              :loading="transferring"
              :disabled="!selectedRecipient"
              data-external="invite-modal-close-btn"
              @click="handleTransfer(selectedRecipient)"
            >
              {{ $t('change_signer') }}
            </FriedButton>
          </FriedModalButtonsFooter>
        </template>
      </FriedModalSubContainer>
    </template>
    <template v-else #container>
      <FriedModalSubContainer v-if="invited">
        <template #header>
          <FriedModalHeader
            :header="$t('invite_sent_title')"
            :description="$t('invite_sent_description', { firstName: selectedRecipient.firstName })"
          />
        </template>
        <template #footer>
          <FriedModalButtonsFooter>
            <FriedButton data-external="invite-modal-close-btn" @click="$emit('close')">
              {{ $t('close') }}
            </FriedButton>
          </FriedModalButtonsFooter>
        </template>
      </FriedModalSubContainer>
      <InviteRecipientForm
        v-else
        :recipient="selectedRecipient"
        :recipients="recipients"
        :loading="inviting || transferring"
        :is-change-signer="isChangeSigner"
        :show-change-signer="showChangeSignerCheckbox"
        :is-transfer-enabled="isTransferEnabled"
        :is-recipient-threshold-reached="isRecipientThresholdReached"
        :error="errorMessage"
        @invite="handleInvite"
        @transfer="handleTransfer"
        @transfer-to-existing="
          handleTransferToExisting({ ...$event, existingRecipientId: $event.id })
        "
        @close="$emit('close')"
      />
    </template>
  </FriedModal>
</template>

<script lang="ts">
import type {
  SigningPageRecipient,
  InviteRecipientData,
} from '@getaccept/lib-shared/src/types/signing-page-recipient';
import type { ComputedRef, PropType } from 'vue';
import { defineComponent, ref, computed } from 'vue';
import { useAsyncState } from '@vueuse/core';
import { RecipientRole } from '@getaccept/lib-shared/src/recipients/enums/recipient-role';
import { RecipientsHelper } from '@getaccept/lib-shared/src/recipients/helpers/recipients.helper';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { useToast } from '@getaccept/fried-tofu';
import { DocumentService } from '../../api/documents/services/document.service';
import { useRecipientSearch } from '../composables/recipient-search.composable';
import { useDocumentStore } from '../../documents/store/document.store';
import { useFieldsStore } from '../../fields/store/fields.store';
import { useInviteAccess } from '../composables/invite-access.composable';
import InviteRecipientForm from './InviteRecipientForm.vue';

export default defineComponent({
  components: { InviteRecipientForm },
  props: {
    recipient: {
      type: Object as PropType<SigningPageRecipient>,
      default: () => ({}),
      required: true,
    },
    recipients: {
      type: Array as PropType<SigningPageRecipient[]>,
      default: () => [],
    },
    isRecipientThresholdReached: Boolean,
    isChangeSigner: Boolean,
  },
  emits: ['close'],
  setup(props) {
    const { isTransferEnabled, showChangeSigner: showChangeSignerCheckbox } = useInviteAccess(
      computed(() => props.recipient)
    );

    const recipientOptions = computed(() =>
      props.recipients
        .filter(({ id }) => id !== props.recipient.id)
        .map(recipient => ({
          firstName: recipient.firstName,
          lastName: recipient.lastName,
          email: recipient.email,
          mobile: recipient.mobile,
          title: recipient.title,
          companyName: recipient.companyName,
          companyNumber: recipient.companyNumber,
          disabled: recipient.role !== RecipientRole.CC,
          tooltip: recipient.role !== RecipientRole.CC ? t('change_signer_disabled_tooltip') : '',
          displayName:
            RecipientsHelper.getRecipientFullName(recipient) || recipient.email || recipient.mobile,
        }))
    );

    const canTransferToViewer = computed(
      () =>
        !isTransferEnabled.value &&
        props.recipients.some(recipient => recipient.role === RecipientRole.CC)
    );

    const showChangeSignerSelectSection = ref(props.isChangeSigner && canTransferToViewer.value);

    const selectedRecipient = ref<InviteRecipientData>();
    const searchInput = ref('');
    const { data: recipientDataFromSearch } = useRecipientSearch(searchInput);

    const handleAddFromSearch = () => {
      selectedRecipient.value = { ...recipientDataFromSearch.value };
      showChangeSignerSelectSection.value = false;
    };

    const handleTransferToExisting = (recipient: SigningPageRecipient) => {
      selectedRecipient.value = recipient;
      showChangeSignerSelectSection.value = true;
    };

    const { loadRecipients } = useDocumentStore();

    const {
      isLoading: inviting,
      isReady: invited,
      error: inviteError,
      execute: invite,
    } = useAsyncState((payload: InviteRecipientData) => DocumentService.invite(payload), null, {
      immediate: false,
      onSuccess: () => {
        loadRecipients();
      },
    });

    const {
      isLoading: transferring,
      isReady: transferred,
      error: transferError,
      execute: transfer,
    } = useAsyncState((payload: InviteRecipientData) => DocumentService.transfer(payload), null, {
      immediate: false,
      onSuccess: () => {
        useFieldsStore().load();
        loadRecipients();
      },
      onError: () => {
        useToast().toast.danger(t('failed-to-invite-recipient'));
      },
    });

    const errorMessage = computed(() => {
      const error = inviteError.value || transferError.value;
      if (!error) return undefined;
      return (error as Error).message === 'Limit for max amount of recipients reached'
        ? {
            title: t('error_toast_cannot_add_more_recipients'),
            message: t('cr_max_recipients_reached_tooltip'),
          }
        : {
            title: t('something-went-wrong'),
            message: t('cr_error_msg_try_again_or_contact_sender'),
          };
    });

    const handleInvite = (payload: InviteRecipientData) => {
      selectedRecipient.value = payload;
      invite(0, payload);
    };

    const handleTransfer = (payload: InviteRecipientData) => {
      selectedRecipient.value = payload;
      transfer(0, payload);
    };

    const modalSize: ComputedRef<'small' | 'large'> = computed(() =>
      invited.value || showChangeSignerSelectSection.value ? 'small' : 'large'
    );

    return {
      errorMessage,
      inviting,
      invited,
      invite,
      handleInvite,
      handleTransfer,
      transferring,
      transferred,
      selectedRecipient,
      modalSize,
      recipientOptions,
      searchInput,
      showChangeSignerSelectSection,
      handleAddFromSearch,
      handleTransferToExisting,
      isTransferEnabled,
      showChangeSignerCheckbox,
    };
  },
});
</script>
<style lang="scss" scoped>
.change-signer-container {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-50);
}
</style>
